import { AbstractVpaDataTableReport } from '../../report/abstract-vpa-data-table-report';
import { addDays, endOfMonth, format, parse, startOfMonth } from 'date-fns';
import { cloneDeep } from 'lodash';
import ActiveFiltersInterface from '@vertical-plus/vue-js-components/lib/model/vos/search/active-filters-interface';
import Client from '@/model/entity/client/client';
import ColumnInterface from '@vertical-plus/vue-js-components/lib/model/vos/report/column-interface';
import FilterInterface from '@vertical-plus/vue-js-components/lib/model/vos/search/filter-interface';
import ServiceContainer from '../../service-container';
import ServiceSearchConditionsInterface from '@/model/vo/billing/service-search-conditions-interface';
import ServiceSearchResultsVo from '@/model/vo/billing/service-search-results-vo';
import SettingInterface from '@vertical-plus/vue-js-components/lib/model/vos/report/setting-interface';
import SortByInterface from '@vertical-plus/vue-js-components/lib/model/vos/report/sort-by-interface';
import WidgetInterface from '@vertical-plus/vue-js-components/lib/model/vos/report/widget-interface';

export default class ServiceDataTableReport extends AbstractVpaDataTableReport
{
    public id = 'services';

    public get widgets(): WidgetInterface[]
    {
        return [];
    }

    public get filters(): FilterInterface[]
    {
        return [
            {
                key: 'id',
                type: 'string',
                title: this.i18n('general.field.id'),
            },
            {
                key: 'client',
                type: 'string',
                title: this.i18n('general.field.client'),
                optionsCallback: async(search: string) => this.serviceContainer.clientService.clientFormOptionsCallback(search),
                titlesCallback: async(values: string[]) => this.serviceContainer.clientService.clientFormTitlesCallback(values),
            },
            {
                key: 'title',
                type: 'string',
                title: this.i18n('general.field.title'),
            },
            {
                key: 'disabled',
                type: 'boolean',
                title: this.i18n('service.field.renew'),
            },
            {
                key: 'active',
                type: 'boolean',
                title: this.i18n('general.field.active'),
            },
        ];
    }

    public get defaultFilters(): ActiveFiltersInterface
    {
        return {
            disabled: true, // Note this gets flipped as the interface says "Renew"
            active: true,
        };
    }

    public get settings(): SettingInterface[]
    {
        return [
            {
                key: 'month',
                title: this.i18n('general.field.month').toString(),
                type: 'date_month',
                clearable: true,
            },
        ];
    }

    public get columns(): ColumnInterface[]
    {
        const columns: ColumnInterface[] = [
            {
                key: 'id',
                title: this.i18n('general.field.id'),
                width: 100,
            },
            {
                key: 'title',
                title: this.i18n('general.field.title'),
            },
        ];

        if (!this.client)
        {
            columns.push({
                key: 'client.name',
                title: this.i18n('general.field.client'),
                width: 250,
            });
        }

        columns.push(...[
            {
                key: 'stage',
                title: this.i18n('general.field.stage'),
                width: 150,
            },
            {
                key: 'dateExpiry',
                title: this.i18n('general.field.date_expiry'),
                width: 120,
            },
            {
                key: 'disabled',
                title: this.i18n('service.field.renew'),
                width: 100,
            },
            {
                key: 'options',
                title: this.i18n('general.field.options'),
                width: 80,
                align: 'end',
            },
        ] as ColumnInterface[]);

        return columns;
    }

    public get defaultColumns(): string[]
    {
        if (this.savedDefaultColumns)
        {
            return this.savedDefaultColumns;
        }

        return [
            'id',
            'title',
            'client.name',
            'stage',
            'dateExpiry',
            'disabled',
            'options',
        ];
    }

    public get sortBys(): SortByInterface[]
    {
        return [
            {
                key: 'id',
                title: this.i18n('general.field.id'),
                order:
                [
                    {
                        key: 'id',
                        descending: false,
                    },
                ],
            },
            {
                key: 'title',
                title: this.i18n('general.field.title'),
                order:
                [
                    {
                        key: 'title',
                        descending: false,
                    },
                ],
            },
            {
                key: 'client.name',
                title: this.i18n('general.field.client'),
                order:
                [
                    {
                        key: 'client.name',
                        descending: false,
                    },
                ],
            },
            {
                key: 'dateExpiry',
                title: this.i18n('general.field.date_expiry'),
                order:
                [
                    {
                        key: 'dateExpiry',
                        descending: false,
                    },
                ],
            },
            {
                key: 'stage',
                title: this.i18n('general.field.stage'),
                order:
                [
                    {
                        key: 'stage',
                        descending: false,
                    },
                ],
            },
            {
                key: 'disabled',
                title: this.i18n('service.field.renew'),
                order:
                [
                    {
                        key: 'disabled',
                        descending: false,
                    },
                ],
            },
        ];
    }

    public defaultSortBy = 'dateExpiry';

    private client: Client|null = null;
    private results: ServiceSearchResultsVo|null = null;

    public constructor(serviceContainer: ServiceContainer, client: Client|null = null)
    {
        super(serviceContainer);

        this.client = client;
    }

    public setClient(client: Client|null)
    {
        this.client = client;
    }

    public async loadResults(conditions: ServiceSearchConditionsInterface): Promise<ServiceSearchResultsVo>
    {
        const searchConditions = cloneDeep(conditions);

        // Force client filter if a client has been defined
        if (this.client?.id)
        {
            searchConditions.client = this.client.id;
        }

        // Flip the disabled filter as the interface is "Renew" (positive)
        if ('disabled' in conditions)
        {
            searchConditions.disabled = !conditions.disabled;
        }

        // Convert month to date range for expiry
        if (conditions.month)
        {
            const date = parse(
                conditions.month, 'yyyy-MM', new Date(),
            );

            searchConditions['dateExpiry[after]'] = format(startOfMonth(date), 'yyyy-MM-dd');
            searchConditions['dateExpiry[strictly_before]'] = format(addDays(endOfMonth(date), 1), 'yyyy-MM-dd');
        }

        const results = await this.serviceContainer.serviceService.findServices(searchConditions);

        this.results = Object.assign({}, results);

        return results;
    }
}